import React from 'react';
import { useIntl } from '../../../util/reactIntl';

import IntegerRangeFilter from '../IntegerRangeFilter/IntegerRangeFilter';

const RADIX = 10;

const hasValue = value => value != null;

const getAgeQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'pub_age';
};

const parse = ageRange => {
  const [minAge, maxAge] = !!ageRange
    ? ageRange.split(',').map(v => Number.parseInt(v, RADIX))
    : [];

  return !!ageRange && Number.isInteger(minAge) && Number.isInteger(maxAge)
    ? { minAge, maxAge }
    : null;
};

const formatAge = (intl, age, maxAge) => {
  const isMaxAge = hasValue(maxAge) && age >= maxAge;

  return isMaxAge
    ? intl.formatMessage({ id: 'AgeFilter.yearPlus' }, { age: maxAge })
    : intl.formatMessage({ id: 'AgeFilter.year' }, { age });
};

const AgeFilter = props => {
  const { label, initialValues, queryParamNames, getAriaLabel = () => {}, max, ...rest } = props;
  const intl = useIntl();

  const formatValidRangeValues = rangeValues => {
    const { minValue, maxValue } = rangeValues;
    return hasValue(minValue) && hasValue(maxValue)
      ? {
          minValue: formatAge(intl, minValue, max),
          maxValue: formatAge(intl, maxValue, max),
        }
      : {};
  };

  const ageQueryParam = getAgeQueryParamName(queryParamNames);
  const initialAge = initialValues?.[ageQueryParam] ? parse(initialValues[ageQueryParam]) : {};
  const { minAge, maxAge } = initialAge || {};

  const hasInitialValues = initialValues && hasValue(minAge) && hasValue(maxAge);
  const formattedMinAge = hasInitialValues ? formatAge(intl, minAge, max) : '';
  const formattedMaxAge = hasInitialValues ? formatAge(intl, maxAge, max) : '';
  const formattedRangeForAriaLabel = hasInitialValues
    ? `${formattedMinAge} - ${formattedMaxAge}`
    : '';
  const labelWithRange = hasInitialValues
    ? intl.formatMessage(
        { id: 'AgeFilter.labelSelectedButton' },
        {
          minAge: formattedMinAge,
          maxAge: formattedMaxAge,
        }
      )
    : label;

  const currentLabel = rest.showAsPopup ? labelWithRange : label;

  const getLabelForRangeInput = (age, handleName) => {
    const formattedAge = formatAge(intl, age, max);
    return intl.formatMessage(
      { id: 'AgeFilter.screenreader.rangeHandle' },
      { formattedAge, handle: handleName }
    );
  };

  return (
    <IntegerRangeFilter
      label={currentLabel}
      initialValues={initialValues}
      formatValidRangeValues={formatValidRangeValues}
      queryParamNames={queryParamNames}
      getLabelForRangeInput={getLabelForRangeInput}
      getAriaLabel={() => getAriaLabel(label, formattedRangeForAriaLabel)}
      max={max}
      {...rest}
    />
  );
};

export default AgeFilter;
